import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  RiFileCopyLine,
  RiLogoutBoxLine,
  RiQrCodeLine,
  RiShieldCheckLine,
} from 'react-icons/ri';
import { apiClient } from '../../../helpers/apiHelper';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { FormField } from '../../ComponnentsUI/common/FormField';

const backendUrl =
  (window as any).RUNTIME_ENV?.BACKEND_URL ??
  process.env.BACKEND_URL ??
  'http://localhost:8000/api/v1';

interface ApiResponse<T = unknown> {
  status: string;
  message: string;
  data: T;
}

const TwoFactorSetupPage = () => {
  const { t } = useTranslation('AccountPage');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [setupData, setSetupData] = useState<{ secret: string; uri: string } | null>(
    null
  );
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = (await apiClient.post('/2fa/setup', {})) as ApiResponse<{
        secret: string;
        uri: string;
      }>;
      setSetupData(res.data);
    } catch (e: { message?: string }) {
      setError(e?.message || t('twoFactor.setupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = (await apiClient.post('/2fa/verify-setup', { code })) as ApiResponse;
      setMessage(res.message || t('twoFactor.enabledSuccess'));
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
      setTimeout(() => navigate('/home'), 2000);
    } catch (e: { message?: string }) {
      setError(e?.message || t('twoFactor.verifyFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post('/logout');
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
    } finally {
      queryClient.clear();
      navigate('/login');
    }
  };

  const logoutButton = (
    <Button
      onClick={handleLogout}
      variant="secondary"
      size="sm"
      className="flex items-center"
    >
      <RiLogoutBoxLine className="w-4 h-4 mr-2" />
      {t('twoFactor.logout')}
    </Button>
  );

  if (!setupData) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <div className="flex justify-end p-4">{logoutButton}</div>
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-6">
                <RiShieldCheckLine className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('twoFactor.requiredTitle')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {t('twoFactor.requiredDescription')}
              </p>
              <Button onClick={startSetup} disabled={loading} className="w-full">
                {loading ? t('twoFactor.settingUp') : t('twoFactor.setUpButton')}
              </Button>
              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex justify-end p-4">{logoutButton}</div>
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg w-full">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <RiShieldCheckLine className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t('twoFactor.setupTitle')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('twoFactor.setupSubtitle')}
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('twoFactor.manualSetupKey')}
                  </h4>
                  <button
                    type="button"
                    onClick={handleCopySecret}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    title={t('twoFactor.copySecretTitle')}
                  >
                    <RiFileCopyLine className="w-4 h-4" />
                  </button>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3 font-mono text-sm break-all">
                  {setupData.secret}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('twoFactor.manualSetupHint')}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('twoFactor.qrCodeTitle')}
                  </h4>
                  <RiQrCodeLine className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-4">
                  <img
                    src={`${backendUrl}/2fa/qr`}
                    alt={t('twoFactor.qrAltShort')}
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  {t('twoFactor.qrCodeHint')}
                </p>
              </div>

              <form onSubmit={verifyCode} className="space-y-4">
                <FormField label={t('twoFactor.verificationCodeLabel')} required>
                  <Input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder={t('twoFactor.codePlaceholder')}
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                    required
                  />
                </FormField>

                <Button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full"
                >
                  {loading ? t('twoFactor.verifying') : t('twoFactor.verifyEnable')}
                </Button>
              </form>

              {message && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-700 dark:text-green-300">{message}</p>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupPage;
